// Central content: services, industries, articles, projects, faqs, service areas

export const services = [
    {
        slug: "new-business-technology-setup",
        number: "01",
        title: "New Business Technology Setup",
        short: "Complete technology setup for openings, renovations and relocations.",
        summary: "One coordinated technology partner instead of a phone tree of unrelated vendors.",
        lead: "A complete technology setup package for businesses opening, expanding or relocating. We handle planning, coordination, installation and handover so your opening day is calmer.",
        deliverables: [
            "Technology planning and site walkthroughs",
            "Equipment coordination",
            "Commercial TV installation and mounting",
            "Digital signage and menu boards",
            "Speakers and audio systems",
            "Wi-Fi access points and network equipment",
            "Workstations, printers, tablets and kiosks",
            "POS hardware coordination",
            "Cable routing and organization",
            "Testing, documentation and staff orientation",
        ],
        image: "https://images.unsplash.com/photo-1538334421852-687c439c92f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1MTk1Njg5fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        slug: "commercial-av-installation",
        number: "02",
        title: "Commercial AV Installation",
        short: "Displays, audio and conferencing systems for real commercial environments.",
        summary: "Meeting rooms, retail displays and hospitality AV that hold up to daily use.",
        lead: "From menu boards to boardrooms, we install commercial-grade AV designed around your staff, customers and operating hours.",
        deliverables: [
            "Commercial displays, TVs and mounting",
            "Projectors and presentation systems",
            "Meeting-room and conference-room AV",
            "Speakers, amplifiers and microphones",
            "Audio distribution",
            "Microsoft Teams Rooms, Zoom Rooms, Google Meet rooms",
        ],
        image: "https://images.pexels.com/photos/3137047/pexels-photo-3137047.jpeg",
    },
    {
        slug: "business-wifi-networking",
        number: "03",
        title: "Business Wi-Fi and Networking",
        short: "Coverage plans, access points and tidy racks that just work.",
        summary: "Reliable coverage where it matters — dining rooms, treatment rooms, sales floors.",
        lead: "We plan and install commercial Wi-Fi so your POS, guests and staff share the network without stepping on each other.",
        deliverables: [
            "Wi-Fi coverage planning",
            "Access-point installation",
            "Router and switch installation",
            "Network organization and rack cleanup",
            "Guest and staff network separation",
            "Connectivity troubleshooting",
            "Network performance assessment",
            "Vendor coordination",
        ],
        note: "Advanced network engineering or regulated electrical work is completed with qualified partners when required.",
        image: "https://images.unsplash.com/photo-1680992046615-065f58bcb4d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMHNlcnZlciUyMHJvb20lMjByYWNrfGVufDB8fHx8MTc4NTE5NTY4OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        slug: "digital-signage-menu-boards",
        number: "04",
        title: "Digital Signage & Menu Boards",
        short: "Menu boards, retail displays and lobby signage installed cleanly.",
        summary: "Bright, aligned, on-brand displays your team can actually update.",
        lead: "Digital menu boards, lobby signage and promotional displays — mounted well, wired cleanly, and simple for staff to manage.",
        deliverables: [
            "Digital menu board installation",
            "Commercial display mounting",
            "Multi-screen layouts",
            "Content-player setup",
            "Promotional and waiting-room displays",
            "Lobby signage and retail product displays",
            "Basic content-system orientation",
        ],
        image: "https://images.pexels.com/photos/2387675/pexels-photo-2387675.jpeg",
    },
    {
        slug: "pos-workstation-coordination",
        number: "05",
        title: "POS & Workstation Coordination",
        short: "Terminals, tablets, printers and back-office setups.",
        summary: "We coordinate the hardware side of your POS vendor's software.",
        lead: "POS terminals, tablets, kitchen display systems, printers and office workstations installed and cabled properly.",
        deliverables: [
            "POS hardware installation",
            "Tablet mounting",
            "Receipt printers",
            "Kitchen display systems",
            "Office workstations",
            "Peripheral setup",
            "Cable organization",
            "Vendor coordination",
            "Testing and staff handover",
        ],
        note: "Final Cut Technologies does not develop or resell third-party POS software.",
        image: "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg",
    },
    {
        slug: "commercial-audio-systems",
        number: "06",
        title: "Commercial Audio Systems",
        short: "Background music, zoned audio and paging for hospitality and retail.",
        summary: "Even volume, clean sources, no random speakers screaming at brunch.",
        lead: "Background music systems, zoned audio for hospitality, and clean amplifier setups that don't fight the room.",
        deliverables: [
            "Background music systems",
            "Zoned audio",
            "Restaurant and café speakers",
            "Hotel common-area audio",
            "Office and retail audio",
            "Amplifier and source setup",
            "Troubleshooting and system cleanup",
        ],
        image: "https://images.pexels.com/photos/4062524/pexels-photo-4062524.jpeg",
    },
    {
        slug: "cable-equipment-organization",
        number: "07",
        title: "Cable & Equipment Organization",
        short: "Cleanup, labelling, rack organization and documentation.",
        summary: "Before/after cable work you'll want to photograph.",
        lead: "The unglamorous work that saves hours later: labelled cables, tidy racks, documented equipment and photos you can hand to any future technician.",
        deliverables: [
            "Cable cleanup",
            "Labelling",
            "Rack organization",
            "Under-counter cable management",
            "Equipment relocation",
            "Removal of abandoned cables where legally permitted",
            "Documentation and photos",
            "Before-and-after examples",
        ],
        image: "https://images.pexels.com/photos/27452443/pexels-photo-27452443.jpeg",
    },
    {
        slug: "technology-troubleshooting-upgrades",
        number: "08",
        title: "Technology Troubleshooting & Upgrades",
        short: "One number when the TVs, Wi-Fi or printers get weird.",
        summary: "Diagnose, resolve, document. Then discuss whether to upgrade.",
        lead: "Display problems, audio dropouts, Wi-Fi complaints, printer chaos and stubborn devices — handled by a technician who has seen it before.",
        deliverables: [
            "Display problems",
            "Audio issues",
            "Wi-Fi complaints",
            "Device connectivity",
            "Printer and workstation setup",
            "Digital signage interruptions",
            "Equipment replacement coordination",
            "Technology refresh projects",
        ],
        image: "https://images.pexels.com/photos/14036272/pexels-photo-14036272.jpeg",
    },
    {
        slug: "managed-support-plans",
        number: "09",
        title: "Managed Technology Support",
        short: "Recurring support with clear response expectations.",
        summary: "Priority scheduling, preventive checks and one point of contact.",
        lead: "Ongoing plans for businesses that would rather have a technology partner than a folder of vendor phone numbers.",
        deliverables: [
            "Essential Support — priority email/phone, remote troubleshooting in business hours, annual review",
            "Proactive Support — quarterly preventive checks, firmware coordination, vendor liaison, discounted labour",
            "Multi-Location Support — centralized contact, standardized installs, per-location documentation",
        ],
        note: "Custom pricing based on location count, system complexity and service requirements.",
        image: "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2xpbmljJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1MTk1Njg4fDA&ixlib=rb-4.1.0&q=85",
    },
];

export const industries = [
    { slug: "restaurants-cafes", title: "Restaurants & Cafés", blurb: "Menu boards, POS, Wi-Fi and background audio for independents, groups, bars and pubs.", image: "https://images.unsplash.com/photo-1538334421852-687c439c92f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1MTk1Njg5fDA&ixlib=rb-4.1.0&q=85" },
    { slug: "hotels-hospitality", title: "Hotels & Hospitality", blurb: "Boutique hotels, small groups, event venues and entertainment spaces.", image: "https://images.pexels.com/photos/14036272/pexels-photo-14036272.jpeg" },
    { slug: "medical-dental-clinics", title: "Medical & Dental Clinics", blurb: "Clinics, dental practices, physiotherapy and wellness offices — including new tenant improvements.", image: "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2xpbmljJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1MTk1Njg4fDA&ixlib=rb-4.1.0&q=85" },
    { slug: "retail-commercial", title: "Retail & Commercial Spaces", blurb: "Boutique retailers, specialty grocers, gyms, studios and professional offices.", image: "https://images.pexels.com/photos/27452443/pexels-photo-27452443.jpeg" },
    { slug: "new-construction-ti", title: "New Construction & TI", blurb: "New builds, tenant improvements, franchise rollouts and mixed-use commercial units.", image: "https://images.pexels.com/photos/3137047/pexels-photo-3137047.jpeg" },
    { slug: "property-managers-developers", title: "Property Managers & Developers", blurb: "Recurring technology support, tenant hand-off and building-wide standards.", image: "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg" },
];

export const caseStudies = [
    {
        slug: "717-west-pender",
        title: "Complete Technology Infrastructure for a Modern Co-Working Office",
        client: "717 West Pender Street",
        industry: "Commercial Office · Co-Working",
        location: "Vancouver, BC",
        year: "2024",
        cover: "/projects/717-west-pender/photo-01-boardroom.png",
        overview: "A newly developed co-working office at 717 West Pender Street needed one technology partner to plan, install and support the building's technology infrastructure before opening. Final Cut Technologies delivered the full deployment — office spaces, meeting rooms, shared work areas and network infrastructure — and continues to provide ongoing support and maintenance today.",
        services: [
            "Structured network cabling",
            "Business Wi-Fi installation and configuration",
            "Network switch and equipment installation",
            "Server rack installation and organization",
            "Commercial printer installation and setup",
            "Computer workstation deployment",
            "Boardroom AV installation",
            "Large-format commercial display installation",
            "Digital signage installation",
            "Projector installation",
            "Cable management and equipment organization",
            "Technology testing and commissioning",
            "Ongoing troubleshooting and technical support",
        ],
        challenge: "As a brand-new commercial office development, every technology system had to be planned, coordinated, installed and tested before tenants moved in. Multiple technologies needed to work together seamlessly while keeping the space clean and professional. The client also wanted a long-term partner who could support the office responsively after opening.",
        solution: "Final Cut Technologies delivered a complete commercial technology solution covering network infrastructure, workstations, meeting spaces, printers, displays, Wi-Fi and audiovisual systems. The boardroom was equipped with modern presentation technology — commercial displays, projection, structured cabling and network connectivity — for day-to-day meetings and hybrid collaboration. Throughout the office, equipment was professionally installed with organized cable management and carefully planned infrastructure for long-term reliability. Following project completion, we continue to provide ongoing technical support, troubleshooting and system maintenance as the client's trusted technology partner.",
        highlights: [
            "Complete technology deployment for a new commercial office",
            "Structured network cabling throughout the workspace",
            "Server rack installation and equipment organization",
            "Business Wi-Fi infrastructure",
            "Commercial printer installation",
            "Computer workstation setup",
            "Executive boardroom AV installation",
            "Commercial displays, projectors and digital signage",
            "Professional cable management",
            "Ongoing technical support and troubleshooting",
        ],
        outcome: "The office opened with a fully integrated technology environment supporting employees, visitors and meeting spaces from day one. Final Cut Technologies continues to provide responsive support and troubleshooting, keeping the client's technology operating reliably as their business evolves.",
        technologies: [
            "Commercial Wi-Fi infrastructure",
            "Structured Ethernet cabling",
            "Network switches",
            "Server rack",
            "Commercial displays",
            "Digital signage",
            "Projectors",
            "Boardroom presentation systems",
            "Office workstations",
            "Commercial printers",
            "Meeting room connectivity",
            "Cable management systems",
        ],
        clientRelationship: {
            label: "Ongoing Managed Support",
            copy: "Following the initial installation, Final Cut Technologies continues to provide on-call troubleshooting, technology support, equipment maintenance and future technology upgrades as required.",
        },
        gallery: [
            { src: "/projects/717-west-pender/photo-01-boardroom.png", caption: "Boardroom — executive boardroom featuring integrated commercial display technology, presentation systems, structured networking and professional AV installation." },
            { src: "/projects/717-west-pender/photo-02-office.png", caption: "Office — modern co-working office equipped with structured cabling, Wi-Fi infrastructure, workstations, commercial printing and organized technology deployment throughout the space." },
        ],
        relatedServices: ["new-business-technology-setup", "business-wifi-networking", "commercial-av-installation"],
        seo: {
            title: "Commercial Office Technology Installation · 717 West Pender Street · Final Cut Technologies",
            description: "Complete commercial office technology installation including structured cabling, Wi-Fi, server rack installation, boardroom AV, commercial displays, printers, workstations and ongoing managed support for a co-working office in downtown Vancouver.",
        },
    },
    {
        slug: "ask-for-luigi",
        title: "Restaurant Technology Installation & Infrastructure",
        client: "Ask for Luigi",
        industry: "Hospitality · Restaurant",
        location: "Vancouver, BC",
        year: "2024",
        cover: "/projects/ask-for-luigi/photo-02.webp",
        overview: "Ask for Luigi needed a dependable technology partner to help support the day-to-day infrastructure behind one of Vancouver's busiest restaurants. The project focused on creating a clean, reliable technology environment that blended into the restaurant's premium interior while supporting staff operations.",
        services: [
            "POS hardware installation",
            "Business Wi-Fi coordination and management",
            "Structured cable routing and cable management",
            "LED lighting installation",
            "Technology setup and onsite coordination",
            "Equipment mounting and finishing",
        ],
        challenge: "Restaurants rely on technology that simply has to work. Staff need dependable Wi-Fi, organized equipment, clean cable routing, and technology that integrates into the customer experience without becoming visually distracting. The installation also had to complement the restaurant's warm, minimalist design while keeping equipment organized and accessible for future maintenance.",
        solution: "Final Cut Technologies coordinated the installation and organization of the restaurant's technology infrastructure — POS hardware, network connectivity, cable management, and LED lighting. Every effort was made to keep visible equipment clean, organized, and discreet so the technology supported the space rather than competing with its design.",
        highlights: [
            "POS installation and setup",
            "Business Wi-Fi support",
            "Professional cable management",
            "LED lighting installation",
            "Clean equipment integration",
            "Hospitality-focused technology coordination",
        ],
        outcome: "A clean, organized technology installation designed to support daily operations while maintaining the premium aesthetic of the dining space. Staff have dependable infrastructure while customer-facing areas stay uncluttered and visually refined.",
        testimonial: {
            quote: "Carlo and his team at Final Cut Technologies did an amazing job at one of our restaurants! The building was old and the cabling, POS, networking and wifi systems were a mess but they came in and made everything perfect. They also replaced our old LED lights and installed new ones as well for the holiday season. We also had all our POS systems go down on us one day and their team was on site in an hour and made sure we had a solution. They are our go-to tech team and we are happy to have them watching over us!",
            attribution: "Ownership team, Ask for Luigi",
        },
        gallery: [
            { src: "/projects/ask-for-luigi/photo-01.webp", caption: "Wall-mounted POS display with concealed cable management and coordinated technology installation." },
            { src: "/projects/ask-for-luigi/photo-02.webp", caption: "Warm hospitality environment showcasing integrated technology that complements the restaurant's interior design." },
        ],
        relatedServices: ["pos-workstation-coordination", "business-wifi-networking", "cable-equipment-organization"],
        seo: {
            title: "Restaurant Technology Installation · Ask for Luigi · Final Cut Technologies",
            description: "Commercial restaurant technology installation including POS setup, Wi-Fi coordination, cable management and LED lighting for Ask for Luigi in Vancouver, BC.",
        },
    },
];

export const projects = [
    { title: "Restaurant Group — Menu Boards & Audio", industry: "Hospitality", city: "Vancouver, BC", scope: "Menu boards, background audio, Wi-Fi refresh", image: "https://images.unsplash.com/photo-1538334421852-687c439c92f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1MTk1Njg5fDA&ixlib=rb-4.1.0&q=85", placeholder: true },
    { title: "Dental Clinic — New Tenant Improvement", industry: "Healthcare", city: "Burnaby, BC", scope: "Full technology package for a new clinic build-out", image: "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2xpbmljJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1MTk1Njg4fDA&ixlib=rb-4.1.0&q=85", placeholder: true },
    { title: "Boutique Hotel — Lobby AV Refresh", industry: "Hospitality", city: "Richmond, BC", scope: "Lobby signage, zoned audio, meeting-room AV", image: "https://images.pexels.com/photos/14036272/pexels-photo-14036272.jpeg", placeholder: true },
    { title: "Specialty Retail — Cable & Rack Cleanup", industry: "Retail", city: "Surrey, BC", scope: "Rack organization, labelling, documentation", image: "https://images.pexels.com/photos/27452443/pexels-photo-27452443.jpeg", placeholder: true },
    { title: "Professional Office — Wi-Fi Redesign", industry: "Offices", city: "Coquitlam, BC", scope: "Coverage plan, access-point install, guest network", image: "https://images.unsplash.com/photo-1680992046615-065f58bcb4d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMHNlcnZlciUyMHJvb20lMjByYWNrfGVufDB8fHx8MTc4NTE5NTY4OXww&ixlib=rb-4.1.0&q=85", placeholder: true },
    { title: "Fitness Studio — Sound & Signage", industry: "Wellness", city: "New Westminster, BC", scope: "Zoned audio, studio displays, POS coordination", image: "https://images.pexels.com/photos/3137047/pexels-photo-3137047.jpeg", placeholder: true },
];

export const faqs = [
    { q: "What areas do you serve?", a: "We serve businesses throughout Metro Vancouver and the Lower Mainland, including Vancouver, Burnaby, Richmond, Surrey, Coquitlam, Port Moody, New Westminster, North and West Vancouver, Delta, Langley and White Rock." },
    { q: "Do you work with new business openings?", a: "Yes — new openings, renovations, expansions and tenant improvements are our specialty. We coordinate with your contractor, designer and other vendors from planning through opening day." },
    { q: "Can you coordinate with our contractor or IT provider?", a: "Yes. We routinely coordinate scope with general contractors, electricians, low-voltage subs, and existing IT providers so nothing falls between the cracks." },
    { q: "Do you supply equipment?", a: "We can supply commercial-grade equipment or install equipment you've already purchased. Either way, we help you avoid mismatched components." },
    { q: "Can you install equipment we already purchased?", a: "Yes, provided it's suitable for commercial use. We'll flag anything we don't think will hold up before installing it." },
    { q: "Do you provide after-hours installation?", a: "Yes — many hospitality and retail installations happen outside of business hours. We schedule around your operations where possible." },
    { q: "Do you provide emergency support?", a: "We provide responsive commercial technology support. Urgent-support availability is confirmed directly per request, and we do not currently offer 24/7 coverage." },
    { q: "Do you work with multiple business locations?", a: "Yes. Multi-location restaurant groups, clinics, retailers and hotels are a core part of our practice." },
    { q: "Do you install residential systems?", a: "No. Final Cut Technologies is strictly a commercial technology company." },
    { q: "Are electrical or structured-cabling permits required?", a: "Requirements are reviewed per project. Regulated electrical work is completed with qualified licensed partners when required." },
    { q: "How are projects priced?", a: "Projects are quoted after a site assessment based on scope, equipment, access and timeline. Support plans are priced based on location count and system complexity." },
    { q: "Do you offer monthly support?", a: "Yes — Essential, Proactive and Multi-Location support plans are available. Custom pricing is confirmed after an assessment." },
    { q: "Can you help document our existing technology?", a: "Yes. We create per-location documentation with photos, labelling and inventory so future work is faster and cheaper." },
    { q: "What happens after installation?", a: "You receive documentation, staff orientation and a clear support relationship — with or without an ongoing plan." },
];

export const articles = [
    { slug: "restaurant-opening-tech-checklist-vancouver", title: "Technology Checklist for Opening a Restaurant in Vancouver", excerpt: "The tech decisions worth making before drywall goes up.", category: "Openings" },
    { slug: "wifi-planning-new-commercial-space", title: "How to Plan Wi-Fi for a New Commercial Space", excerpt: "Coverage, capacity and separation — in plain language.", category: "Networking" },
    { slug: "digital-menu-boards-restaurant-owners", title: "Digital Menu Boards: What Restaurant Owners Should Plan Before Installation", excerpt: "Layouts, brightness, content management and cable paths.", category: "Signage" },
    { slug: "commercial-av-installation-cost", title: "How Much Does Commercial AV Installation Cost?", excerpt: "Educational ranges, not fake precision.", category: "Budgeting" },
    { slug: "seven-signs-your-business-tech-needs-cleanup", title: "Seven Signs Your Business Technology Needs a Professional Cleanup", excerpt: "If more than three sound familiar, it's time.", category: "Maintenance" },
    { slug: "medical-dental-clinic-technology-plan", title: "What to Include in a Medical or Dental Clinic Technology Plan", excerpt: "Workflow-aware technology planning for healthcare tenant improvements.", category: "Healthcare" },
    { slug: "property-manager-tech-problems", title: "How Property Managers Can Reduce Recurring Technology Problems", excerpt: "Standards, documentation and one dependable partner.", category: "Property" },
    { slug: "what-is-managed-technology-support", title: "What Is a Managed Technology Support Plan?", excerpt: "How ongoing plans reduce downtime and vendor confusion.", category: "Support" },
    { slug: "tenant-improvement-technology-planning", title: "Technology Planning for Commercial Tenant Improvements", excerpt: "Where technology fits inside a TI schedule.", category: "Construction" },
    { slug: "av-it-electrical-who-handles-what", title: "AV, IT and Electrical Contractors: Who Handles What?", excerpt: "A field-guide to the trades that touch your systems.", category: "Reference" },
];

export const serviceAreas = [
    { city: "Vancouver", description: "Downtown restaurants, boutique retail, professional offices and clinics across the West End, Yaletown, Mount Pleasant and Kitsilano." },
    { city: "Burnaby", description: "Metrotown and Brentwood commercial spaces, tenant improvements and multi-location retailers." },
    { city: "Richmond", description: "Hospitality-heavy corridors, medical clinics and specialty grocery." },
    { city: "Surrey", description: "New commercial developments, franchise rollouts and clinic build-outs across Guildford, Newton, Cloverdale and South Surrey." },
    { city: "Coquitlam", description: "Tri-Cities offices, wellness studios and multi-tenant commercial buildings." },
    { city: "Port Moody", description: "Boutique retailers and independent hospitality operators." },
    { city: "Port Coquitlam", description: "Community businesses, clinics and studios." },
    { city: "New Westminster", description: "Independent restaurants, cafés and small-format retail." },
    { city: "North Vancouver", description: "Lonsdale corridor hospitality and healthcare tenants." },
    { city: "West Vancouver", description: "Boutique commercial spaces and hospitality." },
    { city: "Delta", description: "Small business retail and restaurants." },
    { city: "Langley", description: "Franchise operators and clinic tenants." },
    { city: "White Rock", description: "Waterfront hospitality and independent retailers." },
];

export const processSteps = [
    { n: "01", title: "Discovery", copy: "Understand the business, location, systems, timeline and priorities." },
    { n: "02", title: "Site Assessment", copy: "Review the space, existing infrastructure and installation requirements." },
    { n: "03", title: "Scope & Estimate", copy: "Provide a clear project scope, recommendations and cost estimate." },
    { n: "04", title: "Installation & Testing", copy: "Coordinate the work, install systems and test everything thoroughly." },
    { n: "05", title: "Support", copy: "Provide documentation, troubleshooting and ongoing support options." },
];

export const supportPlans = [
    {
        name: "Essential",
        tagline: "For small independent businesses.",
        features: [
            "Priority email and phone support",
            "Remote troubleshooting during business hours",
            "Annual technology review",
            "Preferred service scheduling",
            "Basic technology inventory",
        ],
    },
    {
        name: "Proactive",
        tagline: "For growing or multi-location businesses.",
        features: [
            "Faster response target during business hours",
            "Quarterly preventive checks",
            "Remote troubleshooting",
            "Equipment and cable review",
            "Firmware and update coordination",
            "Vendor liaison support",
            "Discounted on-site labour",
        ],
        featured: true,
    },
    {
        name: "Multi-Location",
        tagline: "For restaurant groups, clinics, hotels and retail operators.",
        features: [
            "Centralized support contact",
            "Per-location documentation",
            "Standardized installation practices",
            "Priority scheduling",
            "Preventive site visits",
            "Expansion and opening support",
            "Custom response and service agreement",
        ],
    },
];
